import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ListChecks, CheckCircle2, Circle, Loader2, HeartHandshake, CalendarClock } from "lucide-react";

const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString([], { dateStyle: "medium" }); } catch { return iso; } };

export default function ChecklistPortal() {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/portal/checklist").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const complete = async (id) => {
    setBusyId(id);
    try { await api.post(`/portal/checklist/${id}/complete`); toast.success("Nicely done — we let your guide know."); load(); }
    catch (e) { toast.error(errMsg(e, "Could not update this item.")); }
    finally { setBusyId(null); }
  };

  if (items === null) return <div className="space-y-3"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>;

  const open = items.filter((i) => i.status !== "completed");
  const done = items.filter((i) => i.status === "completed");

  const Item = (c) => (
    <div key={c.id} data-testid={`checklist-item-${c.id}`}
      className={`rounded-2xl border p-4 flex items-start gap-3 ${c.status === "completed" ? "border-[#E6EDF2] bg-[#F4F8FB]" : "border-[#E6EDF2] bg-white"}`}>
      {c.status === "completed"
        ? <CheckCircle2 size={22} className="text-[#2F7D5A] shrink-0 mt-0.5" />
        : <Circle size={22} className="text-[#D6E0E7] shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1">
        <div className={`text-[16px] font-medium ${c.status === "completed" ? "line-through text-[#9AA5B1]" : "text-[#252221]"}`}>{c.title}</div>
        {c.description && <div className="text-[14px] text-muted-foreground mt-0.5">{c.description}</div>}
        {c.due_date && <div className="text-[13px] text-[#B7791F] mt-1 flex items-center gap-1"><CalendarClock size={13} /> Due {fmtDate(c.due_date)}</div>}
      </div>
      {c.status !== "completed" && c.client_can_complete && (
        <Button size="sm" onClick={() => complete(c.id)} disabled={busyId === c.id} className="bg-[#2F7D5A] hover:bg-[#276848] text-white gap-1.5 shrink-0" data-testid={`checklist-complete-${c.id}`}>
          {busyId === c.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark done
        </Button>
      )}
    </div>
  );

  return (
    <div data-testid="checklist-portal-page">
      <h2 className="font-display text-2xl font-bold text-[#252221] mb-1 flex items-center gap-2"><ListChecks size={22} /> Your family checklist</h2>
      <p className="text-[15px] text-muted-foreground mb-5">Small steps, one at a time. Check things off as you go — we'll help with the rest.</p>
      {items.length === 0 ? (
        <Card className="p-10 text-center border-[#E6EDF2]" data-testid="checklist-empty">
          <HeartHandshake size={40} className="mx-auto text-[#1C4358]/40 mb-3" />
          <div className="font-display font-semibold text-[18px] text-[#252221]">Nothing to do right now</div>
          <p className="text-[15px] text-muted-foreground mt-1">Your guide will add your next steps here after your consultation.</p>
        </Card>
      ) : (
        <div className="space-y-5">
          {open.length > 0 && <div className="space-y-2.5">{open.map(Item)}</div>}
          {done.length > 0 && <div><div className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Completed</div><div className="space-y-2.5">{done.map(Item)}</div></div>}
        </div>
      )}
    </div>
  );
}
